import { Link, createFileRoute } from "@tanstack/react-router";
import { GUEST_TIERS, TRIAL, rupiah } from "../../../worker/pricing";
import { LangToggle } from "../components/LangToggle";
import { Logo } from "../components/Logo";
import { useT } from "../lib/i18n";

export const Route = createFileRoute("/")({ component: Landing });

// Tumpukan cetakan B&W di hero (posisi % dari kotak stack, rotasi derajat), sesuai docs/result/home.html.
// Foto stok Wikimedia Commons (lihat public/photos/CREDITS.md untuk kredit & lisensi).
const PRINTS = [
  { src: "sample-portrait", r: -4, left: "0", top: "4%", w: "27%", z: 1, square: false },
  { src: "travel", r: 2, left: "21%", top: "9%", w: "30%", z: 2, square: false },
  { src: "party-2", r: 24, left: "52%", top: "0", w: "35%", z: 3, square: true },
  { src: "wedding", r: 9, left: "58%", top: "46%", w: "38%", z: 2, square: true },
];

const tiers = Object.entries(GUEST_TIERS).map(([g, price]) => ({ guests: Number(g), price }));

const sh = "display text-[clamp(30px,7.5vw,52px)]";
const sub = "mt-1.5 text-sm font-semibold [&_em]:font-medium";

function Landing() {
  const { t } = useT();

  // CONTOH — ganti dengan testimoni asli sebelum rilis prod.
  const TESTIMONIALS = [
    { tag: t("landing.testimonial_tag_wedding"), name: "Rina & Dimas", quote: t("landing.testimonial_quote_1") },
    { tag: t("landing.testimonial_tag_birthday"), name: "Kevin", quote: t("landing.testimonial_quote_2") },
    { tag: t("landing.testimonial_tag_corporate"), name: "Tim HR, PT Sinar", quote: t("landing.testimonial_quote_3") },
  ];

  const STEPS = [
    { title: t("landing.create_album"), body: t("landing.step1_body") },
    { title: t("landing.step2_title"), body: t("landing.step2_body") },
    { title: t("landing.step3_title"), body: t("landing.step3_body") },
  ];

  const FAQ = [
    { q: t("landing.faq1_q"), a: t("landing.faq1_a") },
    { q: t("landing.faq2_q"), a: t("landing.faq2_a") },
    { q: t("landing.faq3_q"), a: t("landing.faq3_a") },
    { q: t("landing.faq4_q"), a: t("landing.faq4_a") },
    { q: t("landing.faq5_q"), a: t("landing.faq5_a") },
  ];

  return (
    <div className="min-h-dvh overflow-x-clip bg-cocoa text-cream [&_:focus-visible]:outline-2 [&_:focus-visible]:outline-offset-3 [&_:focus-visible]:outline-sand">
      <div className="mx-auto w-[min(100%-32px,1040px)]">
        <header className="flex items-center justify-between py-[22px]">
          <Link to="/">
            <Logo />
          </Link>
          <LangToggle className="gap-1.5 text-[13px] tracking-[0.04em] [&_button]:px-0.5 [&_button]:py-1.5 [&_button]:font-semibold [&_button]:text-cream! [&_button[aria-pressed=false]]:opacity-55" />
        </header>

        <main>
          {/* Hero */}
          <section className="pt-3 text-center">
            <h1 className="display text-[clamp(40px,11vw,88px)]">
              {t("landing.hero_title_1")} <span className="script ml-[0.08em] text-[0.72em]">{t("landing.hero_title_is")}</span>
              <br />
              {t("landing.hero_title_2")} <span className="script ml-[0.08em] text-[0.72em]">{t("landing.hero_title_away")}</span>
            </h1>

            <div className="relative mx-auto mt-5 mb-2 aspect-[1/1.02] w-full max-w-[520px]" aria-hidden="true">
              {PRINTS.map((p) => (
                <figure
                  key={p.src}
                  className="absolute rounded-[2px] bg-[#f7f2ee] px-[7px] pt-[7px] pb-[22px] shadow-[0_14px_30px_-12px_rgb(0_0_0/0.55),0_2px_4px_rgb(0_0_0/0.2)]"
                  style={{ left: p.left, top: p.top, width: p.w, zIndex: p.z, transform: `rotate(${p.r}deg)` }}
                >
                  <img
                    src={`/photos/${p.src}.webp`}
                    alt=""
                    className={`w-full object-cover brightness-[1.03] contrast-[1.06] grayscale ${p.square ? "aspect-square" : "aspect-[3/4]"}`}
                  />
                </figure>
              ))}
              {/* Kartu undangan dengan QR ilustratif (bukan kode asli). */}
              <div className="absolute top-[48%] left-[4%] z-[4] flex aspect-[3/3.6] w-[46%] -rotate-[13deg] flex-col justify-between rounded-[2px] bg-[#f4eee9] px-[8%] pt-[9%] pb-[7%] text-left text-cocoa shadow-[0_18px_34px_-14px_rgb(0_0_0/0.6)]">
                <div className="flex justify-between font-display text-[clamp(11px,3vw,18px)] leading-none text-[#8a3a2c] uppercase">
                  <span>Say</span>
                  <span>Yes!</span>
                  <span>21.06</span>
                </div>
                <div className="flex items-end justify-between gap-2 border-t border-[#8a3a2c55] pt-[6%]">
                  <small className="text-[clamp(7px,1.9vw,11px)] leading-[1.3] text-[#6b4a3e]">
                    Scan &amp; snap.
                    <br />
                    Every shot lands
                    <br />
                    in our album.
                  </small>
                  <svg viewBox="0 0 25 25" shapeRendering="crispEdges" fill="currentColor" className="w-[34%] flex-none">
                    <path d="M0 0h7v7H0zM1 1v5h5V1zM2 2h3v3H2zM18 0h7v7h-7zm1 1v5h5V1zm1 1h3v3h-3zM0 18h7v7H0zm1 1v5h5v-5zm1 1h3v3H2zM9 0h2v2H9zm3 1h2v3h-2zm-3 3h2v2H9zm6-4h2v2h-2zM8 8h3v2H8zm5 0h2v3h-2zm4 1h3v2h-3zm4 0h2v2h-2zM0 9h2v2H0zm3 1h3v2H3zm6 2h2v3H9zm3 1h4v2h-4zm6 0h2v4h-2zm3 0h4v2h-4zM9 17h2v2H9zm4-1h2v3h-2zm3 2h2v2h-2zm3 2h3v2h-3zm4-3h2v3h-2zM9 21h3v2H9zm5 1h2v3h-2zm8 1h3v2h-3z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="mx-auto mt-[18px] max-w-[34ch]">
              <h2 className="text-sm font-semibold tracking-[0.04em] uppercase">
                {t("landing.hero_1")} {t("landing.hero_2")}
              </h2>
              <p className="mt-1 text-sm text-cream/85">{t("landing.hero_desc")}</p>
              <Link to="/host" className="pill mt-[22px]">
                {t("landing.cta_try")}
              </Link>
            </div>
          </section>

          {/* Cara kerja */}
          <section id="cara-kerja" aria-labelledby="how" className="scroll-mt-6 pt-24 text-center">
            <h2 id="how" className={sh}>{t("landing.how_it_works_label")}</h2>
            <p className={sub}>
              {t("landing.how_heading_1")} <em>{t("landing.how_heading_em")}</em>
            </p>
            <ol className="mt-[30px] grid gap-3.5 text-left min-[720px]:grid-cols-3">
              {STEPS.map((s, i) => (
                <li
                  key={s.title}
                  className="rounded-2xl bg-cream px-5 pt-[22px] pb-6 text-cocoa shadow-[inset_0_0_0_1.5px_#d7cbbd,0_10px_24px_-16px_rgb(0_0_0/0.6)]"
                >
                  <b className="block font-display text-[52px] leading-[0.8] font-normal text-sand">{i + 1}</b>
                  <h3 className="mt-4 font-display text-[26px] leading-none uppercase">{s.title}</h3>
                  <p className="mt-2 text-[13.5px] text-neutral-600">{s.body}</p>
                </li>
              ))}
            </ol>
          </section>

          {/* Harga */}
          <section aria-labelledby="pricing" className="pt-24 text-center">
            <h2 id="pricing" className={sh}>{t("landing.pricing_label")}</h2>
            <p className={sub}>
              {t("landing.pricing_heading_1")} <em>{t("landing.pricing_heading_em")}</em>
            </p>
            <div className="mx-auto mt-[30px] max-w-[620px] rounded-[28px] bg-cream px-[22px] py-[26px] text-left text-cocoa">
              <div className="flex items-baseline justify-between gap-3 border-b border-dashed border-sand pb-4">
                <strong className="font-display text-[26px] leading-none font-normal uppercase">{t("landing.trial_label")}</strong>
                <span className="text-right text-[13px] text-neutral-500">
                  {t("landing.trial_detail", { guests: TRIAL.guests, days: TRIAL.days })}
                </span>
              </div>
              <ul>
                {tiers.map((tr) => (
                  <li
                    key={tr.guests}
                    className="grid grid-cols-[1fr_auto] items-baseline border-b border-[#e2d8cd] py-[13px] last:border-b-0 last:pb-0"
                  >
                    <span className="text-sm font-semibold">{t("landing.tier_guests", { guests: tr.guests })}</span>
                    <span className="col-start-2 row-span-2 font-display text-[30px] leading-none tabular-nums">{rupiah(tr.price)}</span>
                    <span className="col-start-1 text-xs text-neutral-500">
                      {t("landing.tier_per_guest", { price: rupiah(Math.round(tr.price / tr.guests)) })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="mt-3.5 text-[13px] text-cream/85">{t("landing.pricing_sub")}</p>
            <p className="mt-[30px] text-sm font-semibold">
              {t("landing.ready_1")} <em>{t("landing.ready_em")}</em>
            </p>
            <Link to="/host" className="pill mt-3">
              {t("landing.footer_login")}
            </Link>
          </section>

          {/* Testimoni: kertas bertumpuk */}
          <section aria-labelledby="say" className="pt-24 text-center">
            <h2 id="say" className={sh}>{t("landing.testimonials_label")}</h2>
            <p className={sub}>{t("landing.testimonials_sub")}</p>
            <div className="mt-9 grid gap-[34px] min-[720px]:grid-cols-3">
              {TESTIMONIALS.map((tst, i) => (
                <figure key={tst.name} className="relative text-left">
                  <div
                    aria-hidden="true"
                    className={`absolute inset-0 -translate-x-1 translate-y-1.5 -rotate-[2.5deg] rounded-[2px] bg-sand ${i === 1 ? "min-[720px]:translate-x-1 min-[720px]:rotate-2" : ""}`}
                  />
                  <div className="relative flex h-full flex-col gap-3.5 rounded-[2px] bg-[#f1ebe5] px-5 pt-[22px] pb-5 text-cocoa">
                    <blockquote className="font-display text-[21px] leading-[1.15]">“{tst.quote}”</blockquote>
                    <figcaption className="mt-auto text-xs font-semibold tracking-[0.04em] text-neutral-500 uppercase">
                      {tst.name} · {tst.tag}
                    </figcaption>
                  </div>
                </figure>
              ))}
            </div>
          </section>

          <section className="pt-24 text-center">
            <p className="display text-[clamp(26px,6.6vw,48px)] leading-[1.05]">
              {t("landing.quote_1")} <span className="script text-[0.8em]">{t("landing.quote_em")}</span> {t("landing.quote_2")}
            </p>
          </section>

          {/* FAQ */}
          <section id="faq" aria-labelledby="faq-h" className="scroll-mt-6 pt-24 text-center">
            <h2 id="faq-h" className={sh}>{t("landing.faq_heading_1")}</h2>
            <p className={sub}>{t("landing.faq_heading_em")}</p>
            <div className="mx-auto mt-[26px] max-w-[620px] text-left">
              {FAQ.map((f) => (
                <details key={f.q} className="group border-b border-white/15">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-0.5 py-[18px] text-[15px] font-medium [&::-webkit-details-marker]:hidden">
                    {f.q}
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      className="size-[18px] flex-none fill-none stroke-current stroke-[1.6] [stroke-linecap:round] motion-safe:transition-transform group-open:rotate-45"
                    >
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </summary>
                  <p className="max-w-[60ch] px-0.5 pb-[18px] text-sm text-cream/85">{f.a}</p>
                </details>
              ))}
            </div>
            <p className="mt-11 text-sm font-semibold">
              {t("landing.more_q_1")} <em>{t("landing.more_q_em")}</em>
            </p>
            {/* Belum ada kontak resmi (email/WA) di repo; sementara arahkan ke FAQ. */}
            <a href="#faq" className="pill mt-3">
              {t("landing.contact_us")}
            </a>
          </section>
        </main>

        {/* z-50 menutupi CTA sticky (z-40), jadi footer tak pernah tertutup. */}
        <footer className="relative z-50 mt-24 flex items-center justify-between border-t-[1.5px] border-cream bg-cocoa pt-6 pb-24 md:pb-10">
          <Logo />
          <small className="text-xs text-cream/85">{t("landing.footer_tagline")}</small>
        </footer>
      </div>

      {/* CTA sticky mobile */}
      <div className="fixed inset-x-0 bottom-0 z-40 bg-gradient-to-t from-cocoa via-cocoa/90 px-4 pt-6 pb-3 text-center md:hidden">
        <Link to="/host" className="pill w-full">
          {t("landing.cta_try")}
        </Link>
        <p className="mt-1.5 text-xs text-cream/85">{t("landing.sticky_note", { guests: TRIAL.guests })}</p>
      </div>
    </div>
  );
}
